import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react'

import { clsx } from 'clsx'
import { createPortal } from 'react-dom'

interface DropdownPosition {
  top: number
  left: number
  width: number
}

/**
 * Hook to detect mobile screen size
 */
export function useIsMobile(breakpoint = 640): boolean {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = (): void => setIsMobile(window.innerWidth < breakpoint)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [breakpoint])

  return isMobile
}

export interface PopoverProps {
  /** Whether the popover is open */
  isOpen: boolean
  /** Callback when popover should close */
  onClose: () => void
  /** The trigger element (button, input, etc.) */
  trigger: ReactNode
  /** The popover content */
  children: ReactNode
  /** Title for mobile bottom sheet header */
  mobileTitle?: string
  /** Width of desktop dropdown (default: 'w-[268px]') */
  dropdownWidth?: string
  /** Whether the trigger container should be full width */
  fullWidth?: boolean
  /** Custom class for the container */
  className?: string
  /** Disable closing on outside click */
  disableOutsideClick?: boolean
  /** Alignment of dropdown (default: 'left') */
  align?: 'left' | 'right'
}

// =============================================================================
// Hooks
// =============================================================================

function usePopoverPosition(
  isOpen: boolean,
  isMobile: boolean,
  align: 'left' | 'right',
  containerRef: React.RefObject<HTMLDivElement>,
): DropdownPosition | null {
  const [position, setPosition] = useState<DropdownPosition | null>(null)

  const updatePosition = useCallback(() => {
    /* v8 ignore next */
    if (!containerRef.current) { return }
    const rect = containerRef.current.getBoundingClientRect()
    setPosition({
      top: rect.bottom + window.scrollY + 4,
      left: align === 'right' ? rect.right + window.scrollX : rect.left + window.scrollX,
      width: rect.width,
    })
  }, [align, containerRef])

  useEffect(() => {
    if (isOpen && !isMobile) {
      updatePosition()
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)
      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [isOpen, isMobile, updatePosition])

  return position
}

// eslint-disable-next-line max-params -- TODO(refactor): convert to options object
function useOutsideClick(
  isOpen: boolean,
  isMobile: boolean,
  disableOutsideClick: boolean,
  onClose: () => void,
  containerRef: React.RefObject<HTMLDivElement>,
  dropdownRef: React.RefObject<HTMLDivElement>,
): void {
  useEffect(() => {
    if (disableOutsideClick) { return }

    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target)
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target)
      if (isOutsideContainer && isOutsideDropdown) { onClose() }
    }

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, disableOutsideClick, isMobile, containerRef, dropdownRef])
}

function useEscapeKey(isOpen: boolean, onClose: () => void): void {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') { onClose() }
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])
}

function useBodyScrollLock(isOpen: boolean, isMobile: boolean): void {
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [isOpen, isMobile])
}

// =============================================================================
// Desktop Dropdown
// =============================================================================

interface DesktopDropdownProps {
  dropdownRef: React.RefObject<HTMLDivElement>
  position: DropdownPosition
  align: 'left' | 'right'
  dropdownWidth: string
  children: ReactNode
}

function DesktopDropdown({ dropdownRef, position, align, dropdownWidth, children }: DesktopDropdownProps): JSX.Element {
  return (
    <div
      ref={dropdownRef}
      className={clsx('fixed z-[100]', 'bg-card', 'border border-border', 'rounded-lg shadow-lg', 'animate-scale-in', 'overflow-hidden', dropdownWidth)}
      role="dialog"
      style={{
        top: position.top,
        left: align === 'right' ? 'auto' : position.left,
        right: align === 'right' ? `calc(100vw - ${position.left}px)` : 'auto',
      }}
    >
      {children}
    </div>
  )
}

// =============================================================================
// Mobile Bottom Sheet
// =============================================================================

interface MobileBottomSheetProps {
  mobileTitle: string
  onClose: () => void
  children: ReactNode
}

function MobileBottomSheet({ mobileTitle, onClose, children }: MobileBottomSheetProps): JSX.Element {
  return (
    <div className="fixed inset-0 z-50">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/50 animate-fade-in"
        role="presentation"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') { onClose() } }}
      />
      <div
        className={clsx('absolute bottom-0 left-0 right-0', 'bg-card', 'rounded-t-2xl shadow-xl', 'animate-slide-up', 'flex flex-col', 'max-h-[85vh]')}
        role="dialog"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-10 h-1 bg-muted-foreground/50 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-4 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-foreground">{mobileTitle}</h2>
          <button className="p-2 -mr-2 text-muted-foreground hover:text-foreground" type="button" onClick={onClose}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>
        <div className="h-6 flex-shrink-0" />
      </div>
    </div>
  )
}

// =============================================================================
// Popover
// =============================================================================

/**
 * Popover component that renders as a dropdown on desktop and bottom sheet on mobile.
 *
 * This is a shared component used by DatePicker, TimePicker, SearchableSelect, etc.
 *
 * @example
 * // Basic usage
 * <Popover
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   trigger={<button onClick={() => setIsOpen(true)}>Open</button>}
 *   mobileTitle="Select Option"
 * >
 *   <div>Popover content here</div>
 * </Popover>
 */
export function Popover({
  isOpen,
  onClose,
  trigger,
  children,
  mobileTitle = 'Select',
  dropdownWidth = 'w-[268px]',
  fullWidth = true,
  className,
  disableOutsideClick = false,
  align = 'left',
}: PopoverProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()

  const position = usePopoverPosition(isOpen, isMobile, align, containerRef)
  useOutsideClick(isOpen, isMobile, disableOutsideClick, onClose, containerRef, dropdownRef)
  useEscapeKey(isOpen, onClose)
  useBodyScrollLock(isOpen, isMobile)

  return (
    <div ref={containerRef} className={clsx('relative', fullWidth && 'w-full', className)}>
      {trigger}

      {isOpen && !isMobile && position && createPortal(
        <DesktopDropdown align={align} dropdownRef={dropdownRef} dropdownWidth={dropdownWidth} position={position}>
          {children}
        </DesktopDropdown>,
        document.body
      )}

      {isOpen && isMobile && createPortal(
        <MobileBottomSheet mobileTitle={mobileTitle} onClose={onClose}>
          {children}
        </MobileBottomSheet>,
        document.body
      )}
    </div>
  )
}

/**
 * Content wrapper for popover with consistent padding
 */
export function PopoverContent({
  children,
  className,
  noPadding = false,
}: {
  children: ReactNode
  className?: string
  noPadding?: boolean
}): JSX.Element {
  return (
    <div className={clsx(!noPadding && 'p-3 sm:p-4', className)}>
      {children}
    </div>
  )
}

/**
 * Footer for popover (e.g., Today button, action buttons)
 */
export function PopoverFooter({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}): JSX.Element {
  return (
    <div className={clsx('mt-3 pt-3 border-t border-border', 'sm:mt-3 sm:pt-3', className)}>
      {children}
    </div>
  )
}

/**
 * Action button for popover footer
 */
export function PopoverAction({
  children,
  onClick,
  className,
}: {
  children: ReactNode
  onClick: () => void
  className?: string
}): JSX.Element {
  return (
    <button
      className={clsx(
        'w-full px-3 py-1.5 sm:py-3 text-sm sm:text-base font-medium rounded sm:rounded-lg',
        'text-muted-foreground',
        'hover:bg-accent',
        'sm:bg-accent',
        'active:bg-accent/80',
        'transition-colors duration-150',
        className
      )}
      type="button"
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export default Popover
